import {
  EMPTY_ARRAY,
  formattedDateForSubscription,
  Get,
  urlFromLocalOrAbsolute,
  useSecuritiesContext,
  useSubscription,
  type OHLCData,
  type OHLCVDatapoint,
  type RequestStream,
} from '@talos/kyoko';
import type {
  Bar,
  ErrorCallback,
  HistoryCallback,
  LibrarySymbolInfo,
  OnReadyCallback,
  PeriodParams,
  ResolutionString,
  ResolveCallback,
  SearchSymbolsCallback,
  SubscribeBarsCallback,
  Timezone,
} from '@trading-view/charting_library';
import Big from 'big.js';
import { useCallback, useMemo, useRef, useState, type MutableRefObject } from 'react';
import type { Subscription } from 'rxjs';
import type { TradingViewChartProps } from './TradingViewChart';

const TV_TO_SUB_RESOLUTIONS = {
  '1': '1M',
  '5': '5M',
  '10': '10M',
  '15': '15M',
  '30': '30M',
  '60': '1H',
  '240': '4H',
  '1M': '1MO',
};

const DEFAULT_SUPPORTED_RESOLUTIONS = ['1', '5', '60', '240', '1D'] as ResolutionString[];
const ALL_SUPPORTED_RESOLUTIONS = ['1', '5', '10', '15', '30', '60', '240', '1D', '1W', '1M'] as ResolutionString[];

export const useTradingViewDatafeed = (params: TradingViewChartProps) => {
  const supportedResolutions = params.enableNonNativeOHLCResolutions
    ? ALL_SUPPORTED_RESOLUTIONS
    : DEFAULT_SUPPORTED_RESOLUTIONS;
  const [request, setRequest] = useState<RequestStream | null>(null);
  const { data: sub } = useSubscription(request);
  const subscription = useRef<Subscription>();
  const lastBar: MutableRefObject<(Partial<Bar> & Pick<Bar, 'time'>) | null> = useRef(null);
  const { securitiesBySymbol } = useSecuritiesContext();

  const onReady = useCallback(
    (callback: OnReadyCallback) => {
      setTimeout(() =>
        callback({
          supported_resolutions: supportedResolutions,
        })
      );
    },
    [supportedResolutions]
  );

  const searchSymbols = useCallback(
    (userInput: string, exchange: string, symbolType: string, onResult: SearchSymbolsCallback) => {
      // console.log('searchSymbols', userInput, exchange, symbolType, onResult);
    },
    []
  );

  const resolveSymbol = useCallback(
    (symbolName: string, onResolve: ResolveCallback, onError: ErrorCallback) => {
      const { DefaultPriceIncrement, Description } =
        securitiesBySymbol.get(symbolName) ??
        ({ DefaultPriceIncrement: '0.01', MinPriceIncrement: '0.01' } as {
          DefaultPriceIncrement: string;
          Description?: string;
        });
      const pricescale = Big(1).div(DefaultPriceIncrement).toNumber();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone as Timezone;

      const info: LibrarySymbolInfo = {
        name: params.symbol ?? symbolName,
        ticker: params.symbol ?? symbolName,
        description: Description ?? '',
        type: 'security',
        supported_resolutions: supportedResolutions,
        has_intraday: true,
        has_weekly_and_monthly: params.enableNonNativeOHLCResolutions,
        weekly_multipliers: ['1'],
        monthly_multipliers: ['1'],
        session: '24x7',
        exchange: params.exchangeName,
        listed_exchange: params.exchangeName,
        timezone,
        format: 'price',
        minmov: 1,
        pricescale,
        minmove2: 0,
      };

      setTimeout(() => {
        onResolve(info);
      }, 0);
    },
    [
      params.exchangeName,
      params.symbol,
      params.enableNonNativeOHLCResolutions,
      securitiesBySymbol,
      supportedResolutions,
    ]
  );

  const getBars = useCallback(
    (
      symbolInfo: LibrarySymbolInfo,
      resolution: ResolutionString,
      periodParams: PeriodParams,
      onResult: HistoryCallback,
      onError: ErrorCallback
    ) => {
      const startDate = Sugar.Date.create(periodParams.from * 1000);
      const endDate = Sugar.Date.create(periodParams.to * 1000);
      const subResolution = TV_TO_SUB_RESOLUTIONS[resolution as keyof typeof TV_TO_SUB_RESOLUTIONS] || resolution;
      const markets = params?.markets ?? [];
      const marketsPart = markets.length > 0 ? `/markets/${encodeURIComponent(markets.join(','))}` : '';
      const sd = formattedDateForSubscription(startDate);
      const ed = formattedDateForSubscription(endDate);
      Get(
        urlFromLocalOrAbsolute(params.apiEndpoint),
        `/symbols/${params.symbol}${marketsPart}/ohlcv/${subResolution}?startDate=${sd}&endDate=${ed}`
      )
        .then((res: { data: OHLCVDatapoint[] }) => {
          const bars: (Partial<Bar> & Pick<Bar, 'time'>)[] = res.data.map(d => {
            const empty = [d.Open, d.High, d.Low, d.Close].every(v => v === '0');
            return {
              time: new Date(d['Timestamp']).getTime(),
              open: !empty ? parseFloat(d['Open']) : undefined,
              high: !empty ? parseFloat(d['High']) : undefined,
              low: !empty ? parseFloat(d['Low']) : undefined,
              close: !empty ? parseFloat(d['Close']) : undefined,
              volume: !empty ? parseFloat(d['Volume']) : undefined,
            };
          });
          const isAllEmpty = bars.every(bar => bar.open == null) || res.data.length === 0;
          const noData = isAllEmpty;
          const meta = {
            noData: noData,
            nextTime: noData ? Sugar.Date.create('5 seconds from now').getTime() / 1000 : null,
          };
          const last = isAllEmpty ? null : bars.at(-1);
          if (last != null && (lastBar.current == null || last.time > lastBar.current.time)) {
            lastBar.current = { ...last };
          }
          onResult(isAllEmpty ? EMPTY_ARRAY : (bars as Bar[]), meta);
        })
        .catch(() => {
          onError('An unexpected error occurred.');
        });

      setRequest({
        name: 'OHLCV',
        tag: `OHLCV-${params.symbol}-${subResolution}`,
        Markets: params?.markets ?? [],
        Symbol: params.symbol,
        Resolution: subResolution,
      });
    },
    [params.symbol, params.markets, params.apiEndpoint]
  );

  const subscribeBars = useCallback(
    (symbolInfo: LibrarySymbolInfo, resolution: ResolutionString, onTick: SubscribeBarsCallback) => {
      subscription.current = sub.subscribe(res => {
        const bars = res.data
          .map((d: OHLCData) => {
            return {
              time: new Date(d['Timestamp']).getTime(),
              open: parseFloat(d['Open']),
              high: parseFloat(d['High']),
              low: parseFloat(d['Low']),
              close: parseFloat(d['Close']),
              volume: parseFloat(d['Volume']),
            };
          })
          .filter((bar: Bar) => {
            return (
              bar.time > (lastBar.current?.time || 0) ||
              (bar.time === lastBar.current?.time && (bar?.volume || 0) > (lastBar.current?.volume || 0))
            );
          });

        bars.forEach((bar: Bar) => onTick(bar));
      });
    },
    [sub]
  );

  const unsubscribeBars = useCallback(() => {}, []);

  return useMemo(
    () => ({
      onReady,
      searchSymbols,
      resolveSymbol,
      getBars,
      subscribeBars,
      unsubscribeBars,
    }),
    [onReady, searchSymbols, resolveSymbol, getBars, subscribeBars, unsubscribeBars]
  );
};
